import type { DatabaseLike, DbState } from './types';

const LEADER_STATE_TIMEOUT_MS = 5_000;
const WORKER_STARTUP_TIMEOUT_MS = 45_000;
const LOCAL_DATABASE_URL = '/bible.db';

interface DbVersionSignature {
  etag: string | null;
  contentLength: string | null;
}

interface WorkerResponse {
  id?: string;
  type: 'needs-download' | 'ready' | 'result' | 'error' | 'imported';
  data?: unknown[];
  error?: string;
}

type BcMessage =
  | { kind: 'request-state' }
  | { kind: 'state'; state: DbState }
  | { kind: 'query'; id: string; tabId: string; sql: string; params: unknown[] }
  | { kind: 'result'; id: string; tabId: string; data: unknown[] }
  | { kind: 'error'; id: string; tabId: string; error: string }
  | { kind: 'check-db-update'; id: string; tabId: string }
  | { kind: 'check-db-update-result'; id: string; tabId: string; updated: boolean }
  | { kind: 'check-db-update-error'; id: string; tabId: string; error: string };

export class DatabaseClient implements DatabaseLike {
  status = $state<DbState>({ state: 'idle' });

  private readonly dbSignatureStorageKey = 'kjvreader.db.signature';
  private worker: Worker | null = null;
  private bc: BroadcastChannel | null = null;
  private readonly tabId = crypto.randomUUID();
  private isLeader = false;
  private readonly pending = new Map<
    string,
    { resolve: (rows: unknown[]) => void; reject: (error: Error) => void }
  >();
  private readonly pendingImports = new Map<
    string,
    {
      resolve: () => void;
      reject: (error: Error) => void;
      signature: DbVersionSignature | null;
    }
  >();
  private readonly pendingUpdateChecks = new Map<
    string,
    { resolve: (updated: boolean) => void; reject: (error: Error) => void }
  >();
  private msgId = 0;
  private initPromise: Promise<void> | null = null;
  private initResolve: (() => void) | null = null;
  private initReject: ((error: Error) => void) | null = null;
  private startupDbSignature: DbVersionSignature | null = null;
  private activeDbUpdateCheck: Promise<boolean> | null = null;
  private leaderStateTimeout: ReturnType<typeof setTimeout> | null = null;
  private workerStartupTimeout: ReturnType<typeof setTimeout> | null = null;

  async init(): Promise<void> {
    if (this.status.state === 'ready') {
      return;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.status = { state: 'initializing' };
    if (!this.bc) {
      this.bc = new BroadcastChannel('kjv-db');
      this.bc.addEventListener('message', (event: MessageEvent<BcMessage>) =>
        this.onBcMessage(event.data),
      );
    }

    this.initPromise = new Promise<void>((resolve, reject) => {
      this.initResolve = resolve;
      this.initReject = reject;
      this.tryBecomeLeader();
    }).catch((error) => {
      this.initPromise = null;
      throw error;
    });

    return this.initPromise;
  }

  query<T extends object>(sql: string, params: unknown[] = []): Promise<T[]> {
    const id = String(++this.msgId);
    return new Promise<T[]>((resolve, reject) => {
      this.pending.set(id, {
        resolve: (rows) => resolve(rows as T[]),
        reject,
      });

      if (this.isLeader) {
        this.worker?.postMessage({ id, type: 'query', payload: { sql, params } });
      } else {
        this.bc?.postMessage({ kind: 'query', id, tabId: this.tabId, sql, params });
      }
    });
  }

  checkForUpdates(): Promise<boolean> {
    if (this.isLeader) {
      return this.checkForDbUpdateAsLeader();
    }

    const id = `update:${++this.msgId}`;
    return new Promise<boolean>((resolve, reject) => {
      this.pendingUpdateChecks.set(id, { resolve, reject });
      this.bc?.postMessage({ kind: 'check-db-update', id, tabId: this.tabId });
    });
  }

  private tryBecomeLeader(): void {
    if (!navigator.locks) {
      this.isLeader = true;
      this.createWorker();
      return;
    }

    navigator.locks
      .request('kjv-db-leader', { ifAvailable: true }, async (lock) => {
        if (lock) {
          this.isLeader = true;
          this.createWorker();
          return new Promise<void>(() => {});
        }

        this.bc?.postMessage({ kind: 'request-state' });
        this.startLeaderStateTimeout();
        this.queueForLeadership();
      })
      .catch(() => {
        if (this.isLeader) {
          this.handleLeadershipLost();
          return;
        }

        this.isLeader = true;
        this.createWorker();
      });
  }

  private queueForLeadership(): void {
    if (!navigator.locks) return;

    navigator.locks
      .request('kjv-db-leader', async () => {
        this.promoteToLeader();
        return new Promise<void>(() => {});
      })
      .catch(() => {});
  }

  private promoteToLeader(): void {
    if (this.isLeader) return;

    this.clearLeaderStateTimeout();
    for (const [, entry] of this.pending) {
      entry.reject(new Error('Reconnecting to database'));
    }

    this.pending.clear();
    this.isLeader = true;
    this.createWorker();
  }

  private handleLeadershipLost(): void {
    this.isLeader = false;
    this.worker?.terminate();
    this.worker = null;
    this.clearWorkerStartupTimeout();

    for (const [, entry] of this.pending) {
      entry.reject(new Error('Reconnecting to database'));
    }
    this.pending.clear();

    this.status = { state: 'initializing' };
    this.bc?.postMessage({ kind: 'request-state' });
    this.startLeaderStateTimeout();
    this.queueForLeadership();
  }

  private createWorker(): void {
    this.worker?.terminate();
    this.clearWorkerStartupTimeout();

    this.worker = new Worker(new URL('./db.worker.ts', import.meta.url), {
      type: 'module',
    });
    this.workerStartupTimeout = setTimeout(() => {
      if (this.status.state !== 'initializing') return;

      const message =
        'Database initialization timed out. Reload the app to refresh cached reader assets.';
      this.worker?.terminate();
      this.worker = null;
      this.setStatus({ state: 'error', message });
      this.rejectInit(new Error(message));
    }, WORKER_STARTUP_TIMEOUT_MS);

    this.worker.addEventListener('message', (event: MessageEvent<WorkerResponse>) =>
      this.onWorkerMessage(event.data),
    );

    this.worker.addEventListener('error', (event) => {
      this.clearWorkerStartupTimeout();
      const message = event instanceof ErrorEvent ? event.message : 'Worker error';
      this.setStatus({ state: 'error', message });
      this.rejectInit(new Error(message));
    });
  }

  private setStatus(next: DbState): void {
    this.status = next;
    this.bc?.postMessage({ kind: 'state', state: next });
  }

  private onWorkerMessage(message: WorkerResponse): void {
    this.clearWorkerStartupTimeout();

    if (message.type === 'needs-download') {
      this.downloadAndTransfer().catch((error) => {
        const nextMessage = this.toErrorMessage(error);
        this.startupDbSignature = null;
        this.setStatus({ state: 'error', message: nextMessage });
        this.rejectInit(error instanceof Error ? error : new Error(nextMessage));
      });
      return;
    }

    if (message.type === 'ready') {
      if (this.startupDbSignature) {
        this.writeStoredDbSignature(this.startupDbSignature);
        this.startupDbSignature = null;
      }

      this.setStatus({ state: 'ready' });
      this.resolveInit();
      return;
    }

    if (message.type === 'imported' && message.id) {
      const entry = this.pendingImports.get(message.id);
      if (!entry) return;

      this.pendingImports.delete(message.id);
      if (entry.signature) {
        this.writeStoredDbSignature(entry.signature);
      }

      entry.resolve();
      return;
    }

    if (message.type === 'error' && !message.id) {
      const nextMessage = message.error ?? 'Unknown error';
      this.startupDbSignature = null;
      this.setStatus({ state: 'error', message: nextMessage });
      this.rejectInit(new Error(nextMessage));
      return;
    }

    if (message.type === 'error' && message.id) {
      const importEntry = this.pendingImports.get(message.id);
      if (importEntry) {
        this.pendingImports.delete(message.id);
        importEntry.reject(new Error(message.error ?? 'Database import failed'));
        return;
      }
    }

    const entry = message.id ? this.pending.get(message.id) : undefined;
    if (!message.id || !entry) return;

    this.pending.delete(message.id);
    if (message.type === 'result') {
      entry.resolve(message.data ?? []);
    } else {
      entry.reject(new Error(message.error ?? 'Query error'));
    }
  }

  private onBcMessage(message: BcMessage): void {
    if (this.isLeader) {
      if (message.kind === 'request-state') {
        this.bc?.postMessage({ kind: 'state', state: this.status });
      } else if (message.kind === 'query') {
        this.proxyFollowerQuery(message);
      } else if (message.kind === 'check-db-update') {
        void this.proxyFollowerDbUpdateCheck(message);
      }

      return;
    }

    if (message.kind === 'state') {
      this.status = message.state;
      if (message.state.state === 'ready') {
        this.clearLeaderStateTimeout();
        this.resolveInit();
      } else if (message.state.state === 'error') {
        this.clearLeaderStateTimeout();
        this.rejectInit(new Error(message.state.message));
      } else if (message.state.state === 'initializing') {
        this.startLeaderStateTimeout();
      } else {
        this.clearLeaderStateTimeout();
      }
      return;
    }

    if ((message.kind === 'result' || message.kind === 'error') && message.tabId === this.tabId) {
      const entry = this.pending.get(message.id);
      if (!entry) return;

      this.pending.delete(message.id);
      if (message.kind === 'result') {
        entry.resolve(message.data);
      } else {
        entry.reject(new Error(message.error));
      }
      return;
    }

    if (message.kind === 'check-db-update-result' && message.tabId === this.tabId) {
      const entry = this.pendingUpdateChecks.get(message.id);
      if (!entry) return;

      this.pendingUpdateChecks.delete(message.id);
      entry.resolve(message.updated);
      return;
    }

    if (message.kind === 'check-db-update-error' && message.tabId === this.tabId) {
      const entry = this.pendingUpdateChecks.get(message.id);
      if (!entry) return;

      this.pendingUpdateChecks.delete(message.id);
      entry.reject(new Error(message.error));
    }
  }

  private resolveInit(): void {
    this.initResolve?.();
    this.initResolve = null;
    this.initReject = null;
  }

  private rejectInit(error: Error): void {
    this.initReject?.(error);
    this.initResolve = null;
    this.initReject = null;
  }

  private startLeaderStateTimeout(): void {
    this.clearLeaderStateTimeout();
    this.leaderStateTimeout = setTimeout(() => {
      if (this.isLeader || this.status.state !== 'initializing') return;
      this.stealLeadership();
    }, LEADER_STATE_TIMEOUT_MS);
  }

  private clearLeaderStateTimeout(): void {
    if (!this.leaderStateTimeout) return;

    clearTimeout(this.leaderStateTimeout);
    this.leaderStateTimeout = null;
  }

  private stealLeadership(): void {
    if (!navigator.locks) {
      this.promoteToLeader();
      return;
    }

    navigator.locks
      .request('kjv-db-leader', { steal: true }, async (lock) => {
        if (!lock) {
          throw new Error('Database leader lock was unavailable.');
        }

        this.promoteToLeader();
        return new Promise<void>(() => {});
      })
      .catch((error) => {
        if (this.isLeader) {
          this.handleLeadershipLost();
          return;
        }

        const message = `Database leader did not respond: ${this.toErrorMessage(error)}`;
        this.setStatus({ state: 'error', message });
        this.rejectInit(new Error(message));
      });
  }

  private clearWorkerStartupTimeout(): void {
    if (!this.workerStartupTimeout) return;

    clearTimeout(this.workerStartupTimeout);
    this.workerStartupTimeout = null;
  }

  private proxyFollowerQuery(message: Extract<BcMessage, { kind: 'query' }>): void {
    const proxyId = `proxy:${message.tabId}:${message.id}`;
    this.pending.set(proxyId, {
      resolve: (rows) => {
        this.bc?.postMessage({
          kind: 'result',
          id: message.id,
          tabId: message.tabId,
          data: rows,
        });
      },
      reject: (error) => {
        this.bc?.postMessage({
          kind: 'error',
          id: message.id,
          tabId: message.tabId,
          error: String(error),
        });
      },
    });

    this.worker?.postMessage({
      id: proxyId,
      type: 'query',
      payload: { sql: message.sql, params: message.params },
    });
  }

  private async proxyFollowerDbUpdateCheck(
    message: Extract<BcMessage, { kind: 'check-db-update' }>,
  ): Promise<void> {
    try {
      const updated = await this.checkForDbUpdateAsLeader();
      this.bc?.postMessage({
        kind: 'check-db-update-result',
        id: message.id,
        tabId: message.tabId,
        updated,
      });
    } catch (error) {
      this.bc?.postMessage({
        kind: 'check-db-update-error',
        id: message.id,
        tabId: message.tabId,
        error: this.toErrorMessage(error),
      });
    }
  }

  private checkForDbUpdateAsLeader(): Promise<boolean> {
    if (this.activeDbUpdateCheck) {
      return this.activeDbUpdateCheck;
    }

    this.activeDbUpdateCheck = this.runDbUpdateCheck().finally(() => {
      this.activeDbUpdateCheck = null;
    });

    return this.activeDbUpdateCheck;
  }

  private async runDbUpdateCheck(): Promise<boolean> {
    const remoteSignature = await this.fetchRemoteDbSignature();
    if (!this.shouldRefreshDatabase(remoteSignature)) {
      return false;
    }

    const download = await this.fetchDatabaseDownload(false);
    await this.importDownloadedDatabase(download.buffer, download.signature ?? remoteSignature);
    return true;
  }

  private async downloadAndTransfer(): Promise<void> {
    const download = await this.fetchDatabaseDownload(true);
    this.startupDbSignature = download.signature;
    this.worker?.postMessage({ type: 'import-db', payload: { buffer: download.buffer } }, [
      download.buffer,
    ]);
  }

  private async fetchDatabaseDownload(reportProgress: boolean): Promise<{
    buffer: ArrayBuffer;
    signature: DbVersionSignature | null;
  }> {
    if (reportProgress) {
      this.setStatus({ state: 'downloading', progress: 0 });
    }

    const response = await this.fetchDatabaseResource({ cache: 'no-store' });
    if (!response.ok) {
      throw new Error(`Download failed: HTTP ${response.status}`);
    }

    const total = Number(response.headers.get('content-length') ?? 0);
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Database download body was unavailable.');
    }

    const chunks: Uint8Array[] = [];
    let received = 0;

    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      received += value.length;

      if (reportProgress && total > 0) {
        this.setStatus({ state: 'downloading', progress: received / total });
      }
    }

    const buffer = new Uint8Array(received);
    let offset = 0;

    for (const chunk of chunks) {
      buffer.set(chunk, offset);
      offset += chunk.length;
    }

    return {
      buffer: buffer.buffer,
      signature: this.readSignatureFromHeaders(response.headers),
    };
  }

  private async importDownloadedDatabase(
    buffer: ArrayBuffer,
    signature: DbVersionSignature | null,
  ): Promise<void> {
    const id = `import:${++this.msgId}`;
    const completion = new Promise<void>((resolve, reject) => {
      this.pendingImports.set(id, { resolve, reject, signature });
    });

    this.worker?.postMessage({ id, type: 'import-db', payload: { buffer } }, [buffer]);
    await completion;
  }

  private async fetchRemoteDbSignature(): Promise<DbVersionSignature | null> {
    const response = await this.fetchDatabaseResource({ method: 'HEAD', cache: 'no-store' });
    if (!response.ok) {
      throw new Error(`Database version check failed: HTTP ${response.status}`);
    }

    return this.readSignatureFromHeaders(response.headers);
  }

  private shouldRefreshDatabase(remoteSignature: DbVersionSignature | null): boolean {
    if (!remoteSignature || !this.hasComparableSignature(remoteSignature)) {
      return false;
    }

    const localSignature = this.readStoredDbSignature();
    if (!localSignature) {
      return true;
    }

    return (
      remoteSignature.etag !== localSignature.etag ||
      remoteSignature.contentLength !== localSignature.contentLength
    );
  }

  private readStoredDbSignature(): DbVersionSignature | null {
    if (typeof localStorage === 'undefined') {
      return null;
    }

    const raw = localStorage.getItem(this.dbSignatureStorageKey);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as Partial<DbVersionSignature>;
      return {
        etag: typeof parsed.etag === 'string' ? parsed.etag : null,
        contentLength: typeof parsed.contentLength === 'string' ? parsed.contentLength : null,
      };
    } catch {
      return null;
    }
  }

  private writeStoredDbSignature(signature: DbVersionSignature): void {
    if (typeof localStorage === 'undefined' || !this.hasComparableSignature(signature)) {
      return;
    }

    localStorage.setItem(this.dbSignatureStorageKey, JSON.stringify(signature));
  }

  private readSignatureFromHeaders(headers: Headers): DbVersionSignature | null {
    const signature = {
      etag: headers.get('etag'),
      contentLength: headers.get('content-length'),
    };

    return this.hasComparableSignature(signature) ? signature : null;
  }

  private hasComparableSignature(signature: DbVersionSignature): boolean {
    return Boolean(signature.etag || signature.contentLength);
  }

  private toErrorMessage(error: unknown): string {
    return error instanceof Error ? error.message : String(error);
  }

  private async fetchDatabaseResource(init: RequestInit): Promise<Response> {
    return fetch(LOCAL_DATABASE_URL, init);
  }
}
